//src/pages/inventory/Product/OCRDialog.jsx
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Paper,
} from "@mui/material";
import {
  CloudUpload,
  CheckCircle,
} from "@mui/icons-material";
import { processInvoiceWithOCR } from "../../../services/ocrService";
import {
  OCR_UPLOAD_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
} from "../../../utils/formModalStyles";

const OCRDialog = ({ open, onClose, onOCRComplete }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [retryInfo, setRetryInfo] = useState(null); // Track retry status
  const [canRetry, setCanRetry] = useState(false); // Track if manual retry is available

  const handleFileSelect = (file) => {
    if (file && (file.type.startsWith('image/') || file.type === 'application/pdf')) {
      setSelectedFile(file);
      setError("");
    } else {
      setError("Please select a valid image file (JPG, PNG, GIF, WebP) or PDF file");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Extract retry delay from error message if available
  const extractRetryDelayFromMessage = (message) => {
    const match = message.match(/wait (\d+) second/i);
    return match ? parseInt(match[1]) * 1000 : null;
  };

  const processOCR = async (isManualRetry = false) => {
    if (!selectedFile) {
      setError("Please select a file first");
      return;
    }

    // If manual retry, check if we need to wait
    if (isManualRetry && error) {
      const retryDelay = extractRetryDelayFromMessage(error);
      if (retryDelay && retryDelay > 0) {
        setError(`Please wait ${Math.ceil(retryDelay / 1000)} more seconds before retrying...`);
        setUploading(true);
        setUploadProgress(90);
        
        // Wait for the delay
        for (let remaining = Math.ceil(retryDelay / 1000); remaining > 0; remaining--) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          setError(`Please wait ${remaining} more second${remaining !== 1 ? 's' : ''} before retrying...`);
        }
        
        setError("");
        setUploadProgress(0);
      }
    }

    setUploading(true);
    setUploadProgress(0);
    setError("");
    setRetryInfo(null);
    setCanRetry(false);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Convert file to base64 (handle PDF conversion)
      let base64;
      let fileType = selectedFile.type;
      
      if (selectedFile.type === 'application/pdf') {
        try {
          // Convert PDF to image first
          console.log("Converting PDF to image...");
          base64 = await convertPdfToImage(selectedFile);
          fileType = 'image/jpeg'; // Converted to JPEG
          console.log("PDF conversion successful");
        } catch (pdfError) {
          console.error("PDF conversion failed:", pdfError);
          clearInterval(progressInterval);
          throw new Error("Failed to convert PDF to image. Please try using an image file instead or take a screenshot of the PDF.");
        }
      } else {
        // Regular image file
        base64 = await fileToBase64(selectedFile);
      }
      
      // Call Gemini API for OCR processing with progress callback
      console.log("Starting OCR processing with file type:", fileType);
      
      // Progress callback to show retry status
      const progressCallback = (progress) => {
        if (progress.message) {
          setError(""); // Clear any previous errors
          console.log("OCR Progress:", progress.message);
          
          // Update retry info if retrying
          if (progress.retrying) {
            setRetryInfo({
              message: progress.message,
              delay: progress.retryDelay,
              attempt: progress.attempt,
              maxRetries: progress.maxRetries
            });
            // Update progress to show retry status (keep it at 90% during retry)
            setUploadProgress(90);
          } else {
            setRetryInfo(null);
          }
        }
      };
      
      const ocrResult = await processInvoiceWithOCR(base64, fileType, progressCallback);
      console.log("OCR processing completed:", ocrResult);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setRetryInfo(null);
      setCanRetry(false);
      
      // Simulate processing delay
      setTimeout(() => {
        setUploading(false);
        onOCRComplete(ocrResult);
        handleClose();
      }, 1000);

    } catch (error) {
      console.error("OCR processing error:", error);
      
      // Show user-friendly error message (already formatted by ocrService)
      const errorMessage = error.message || "OCR processing failed";
      
      setError(errorMessage);
      setUploading(false);
      setUploadProgress(0);
      setRetryInfo(null);
      
      // Enable retry button if it's a rate limit error (but not daily quota exhausted)
      const isRateLimitError = errorMessage.includes("rate limit") || errorMessage.includes("quota");
      const isDailyQuotaExhausted = error.isDailyQuota === true;
      
      // Only enable retry if it's not a daily quota (which won't reset until tomorrow)
      // For daily quota, user needs to wait or upgrade plan
      setCanRetry(isRateLimitError && !isDailyQuotaExhausted);
      
      // If daily quota is exhausted, provide additional guidance
      if (isDailyQuotaExhausted) {
        console.warn("Daily quota exhausted. User should wait until tomorrow or upgrade plan.");
      }
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const convertPdfToImage = async (pdfFile) => {
    try {
      // Load PDF.js from CDN dynamically
      if (!window.pdfjsLib) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
          script.onload = () => {
            // Set up the worker
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
            resolve();
          };
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      
      const arrayBuffer = await pdfFile.arrayBuffer();
      const pdf = await window.pdfjsLib.getDocument(arrayBuffer).promise;
      
      // Get the first page
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 2.0 }); // Higher scale for better quality
      
      // Create canvas
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      // Render PDF page to canvas
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      // Convert canvas to base64 image
      return canvas.toDataURL('image/jpeg', 0.9);
    } catch (error) {
      console.error('Error converting PDF to image:', error);
      throw new Error('Failed to convert PDF to image. Please try using an image file instead.');
    }
  };


  const handleClose = () => {
    setSelectedFile(null);
    setUploading(false);
    setUploadProgress(0);
    setError("");
    setDragActive(false);
    setRetryInfo(null);
    setCanRetry(false);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md" 
      fullWidth
      scroll="paper"
      sx={OCR_UPLOAD_DIALOG_PAPER_SX}
    >
      <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>
        Upload Medicine Invoice (Image or PDF)
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Upload a clear image or PDF of your medicine invoice. The system will automatically extract all details including medicine names, quantities, batch numbers, expiry dates, and supplier information.
          </Typography>
          
          {/* File Upload Area */}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              border: dragActive ? '2px dashed #1976d2' : '2px dashed #ccc',
              backgroundColor: dragActive ? '#f5f5f5' : '#fafafa',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#f0f0f0',
                borderColor: '#1976d2'
              }
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => document.getElementById('file-input').click()}
          >
            <input
              id="file-input"
              type="file"
              accept="image/*,application/pdf"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            
            {selectedFile ? (
              <Box>
                <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  File Selected
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
            ) : (
              <Box>
                <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Drop your invoice here (Image or PDF)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  or click to browse files
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Supported formats: JPG, PNG, GIF, WebP, PDF
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {retryInfo ? retryInfo.message : "Processing invoice..."}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={uploadProgress} 
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {retryInfo ? retryInfo.message : `${uploadProgress}% complete`}
            </Typography>
          </Box>
        )}

        {/* Error Display */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            action={
              canRetry && !uploading && (
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={() => processOCR(true)}
                  disabled={uploading || !selectedFile}
                >
                  Retry
                </Button>
              )
            }
          >
            <Box>
              <Typography variant="body2">{error}</Typography>
              {error.includes("daily quota") && (
                <Typography variant="caption" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                  💡 Tip: Your daily quota resets at midnight. Consider upgrading your Gemini API plan for higher limits.
                </Typography>
              )}
            </Box>
          </Alert>
        )}

        {/* Instructions */}
        <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#1976d2' }}>
            📋 Tips for Best Results:
          </Typography>
          <Typography variant="body2" color="textSecondary" component="div">
            ✓ Ensure the invoice is clearly visible and well-lit<br/>
            ✓ Make sure all text is readable and not handwritten<br/>
            ✓ Include complete details: medicine names, quantities, batch numbers, expiry dates<br/>
            ✓ Capture supplier information at the top of invoice<br/>
            ✓ Avoid blurry, rotated, or folded images<br/>
            ✓ Supported formats: JPG, PNG, GIF, WebP, PDF
          </Typography>
        </Box>
        
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> After processing, you'll be able to review and edit all extracted data before adding to inventory. Expired or near-expiry items will be highlighted for your review.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
        <Button 
          onClick={handleClose} 
          color="primary" 
          variant="outlined"
          sx={FORM_CANCEL_BUTTON_SX}
          disabled={uploading}
        >
          Cancel
        </Button>
        <Button 
          onClick={processOCR} 
          color="primary" 
          variant="contained"
          sx={FORM_SUBMIT_BUTTON_SX}
          disabled={!selectedFile || uploading}
        >
          {uploading ? 'Processing...' : 'Process Invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OCRDialog;
